import React, { useRef, useEffect } from 'react';
import useClassNames from '@medflex/hooks/useClassNames';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { isMobilePhoneNumber, EMAIL_REGEX } from '@medflex/shared-utils';
import * as yup from 'yup';
import * as PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import hasUmlauts from '@medflex/utils/hasUmlauts';
import Button from '../Button';
import InputField from '../InputField';
import { IconMenu, IconLink, IconQrCode } from '../Icons';
import SwitchButton, { Switch } from '../SwitchButton';
import PhoneNumber from '../PhoneNumber';
import FormErrorMessage from '../FormErrorMessage';
import SMSFeatureNotice from '../SMSFeatureNotice/SMSFeatureNotice';
import Link from '../Link';

const InvitePatientForm = ({
  onSubmit,
  isLoading,
  status,
  className,
  onClickMultiPatientUpload,
  email,
  firstName,
  lastName,
  isMobileNumberMode,
  onClickPermanentLink,
  onClickQRCodeLink,
  onModeSwitch,
  autofocus,
  canSendSms,
  smsUpgradeUrl,
}) => {
  const cn = useClassNames(() => ['flex', 'flex-col', 'max-h-full', className], [className]);
  const { t } = useTranslation('components');
  const ref = useRef();

  const schemaEmail = yup.object().shape({
    firstName: yup
      .string()
      .trim()
      .required(t('global:validation.messages.required'))
      .label('Vorname'),
    lastName: yup
      .string()
      .trim()
      .required(t('global:validation.messages.required'))
      .label('Nachname'),
    email: yup
      .string()
      .required(t('global:validation.messages.required'))
      .test('', t('global:validation.messages.emailUmlauts'), value => !hasUmlauts(value))
      .matches(EMAIL_REGEX, t('global:validation.messages.email'))
      .label('E-Mail-Adresse'),
  });

  const schemaSMS = yup.object().shape({
    mobileNumber: yup
      .string()
      .required(t('global:validation.messages.required'))
      .test('has valid format', t('global:validation.messages.mobileNumber'), value =>
        isMobilePhoneNumber(value),
      )
      .label('Mobilnummer'),
  });

  const {
    register: registerFormElement,
    handleSubmit: wrapSubmit,
    control,
    errors,
    formState,
  } = useForm({
    resolver: yupResolver(isMobileNumberMode ? schemaSMS : schemaEmail),
    mode: 'onChange',
  });

  const { isValid } = formState;

  const handleSubmit = formData => {
    const { email: mail, mobileNumber: sms, ...rest } = formData;
    onSubmit({
      ...(isMobileNumberMode ? { mobileNumber: sms } : { email: mail }),
      ...rest,
    });
  };

  useEffect(() => {
    if (ref.current) {
      registerFormElement(ref.current);
      if (autofocus) ref.current.focus();
    }
  }, [ref, autofocus]);

  return (
    <div data-component="InvitePatientForm" className={cn}>
      <form onSubmit={wrapSubmit(handleSubmit)} className="bg-slate-100 p-4 rounded-sm mb-8">
        <Switch onChange={onModeSwitch} className="mb-4">
          <SwitchButton value="email" checked={!isMobileNumberMode}>
            {t('InvitePatientForm.typeSwitch.email')}
          </SwitchButton>
          <div className="ml-2" />
          <SwitchButton value="sms" checked={isMobileNumberMode}>
            {t('InvitePatientForm.typeSwitch.phone')}
          </SwitchButton>
        </Switch>
        <div className="flex-1">
          {!canSendSms && isMobileNumberMode ? (
            <SMSFeatureNotice
              title={t('InvitePatientForm.smsNotice.title')}
              description={t('InvitePatientForm.smsNotice.description')}
            >
              <Link
                href={smsUpgradeUrl}
                target="_blank"
                rel="noreferrer"
                kind="button"
                className="text-lg"
              >
                {t('InvitePatientForm.smsNotice.cta')}
              </Link>
            </SMSFeatureNotice>
          ) : (
            <>
              <fieldset className="mb-6">
                <div hidden={isMobileNumberMode} className="mb-6 space-y-1">
                  <label className="block font-semibold" htmlFor="firstName">
                    {t('InvitePatientForm.first_name.label')}
                  </label>
                  <InputField
                    id="firstName"
                    readOnly={isLoading}
                    defaultValue={firstName}
                    name="firstName"
                    innerRef={ref}
                    placeholder={t('InvitePatientForm.first_name.placeholder')}
                  />
                  {errors.firstName && <FormErrorMessage message={errors.firstName.message} />}
                </div>
                <div className="mb-6 space-y-1" hidden={isMobileNumberMode}>
                  <label className="block font-semibold" htmlFor="lastName">
                    {t('InvitePatientForm.last_name.label')}
                  </label>
                  <InputField
                    ref={registerFormElement}
                    id="lastName"
                    readOnly={isLoading}
                    defaultValue={lastName}
                    name="lastName"
                    placeholder={t('InvitePatientForm.last_name.placeholder')}
                  />
                  {errors.lastName && <FormErrorMessage message={errors.lastName.message} />}
                </div>
                <div className="space-y-1 mb-1" hidden={isMobileNumberMode}>
                  <label className="block font-semibold" htmlFor="email">
                    {t('InvitePatientForm.email.label')}
                  </label>
                  <InputField
                    ref={registerFormElement}
                    id="email"
                    readOnly={isLoading}
                    name="email"
                    defaultValue={email}
                    type="email"
                    placeholder={t('InvitePatientForm.email.placeholder')}
                  />
                  {errors.email && <FormErrorMessage message={errors.email.message} />}
                </div>
                <div className="space-y-1 mb-1" hidden={!isMobileNumberMode}>
                  <label className="block font-semibold" htmlFor="mobileNumber">
                    {t('InvitePatientForm.mobile_number.label')}
                  </label>
                  <PhoneNumber
                    name="mobileNumber"
                    control={control}
                    placeholder={t('InvitePatientForm.mobile_number.placeholder')}
                    type="mobile"
                  />
                  {errors.mobileNumber && (
                    <FormErrorMessage message={errors.mobileNumber.message} />
                  )}
                </div>
                <p className="text-sm text-slate-600">
                  {!isMobileNumberMode && t('InvitePatientForm.email.info')}
                  {isMobileNumberMode && t('InvitePatientForm.mobile_number.info')}
                </p>
              </fieldset>
              {status && status.apiError && (
                <FormErrorMessage
                  className="text-center mb-2"
                  message={t(`InvitePatientForm.error.${status.apiError}`)}
                />
              )}
              <div className="flex justify-end">
                <Button
                  className="flex-none"
                  isLoading={isLoading}
                  isDisabled={!isValid}
                  type="submit"
                >
                  {t('InvitePatientForm.submit_button.label')}
                </Button>
              </div>
            </>
          )}
        </div>
      </form>
      <div className="mb-8 flex w-full items-center text-slate-600">
        <div className="border-t h-0 flex-1 border-slate-400" />
        <div className="mx-4">{t('InvitePatientForm.options')}</div>
        <div className="border-t h-0 flex-1 border-slate-400" />
      </div>
      <div className="pb-8 lg:pb-0 flex lg:items-center justify-between flex-col lg:flex-row">
        <button
          type="button"
          onClick={onClickQRCodeLink}
          className="flex focus:outline-none flex-row lg:flex-col group text-slate-600 items-center mb-4 lg:mb-0"
        >
          <div className="w-6 h-6 fill-current inline-block lg:mb-2">
            <IconQrCode className="fill-current" />
          </div>
          <div className="text-blue-500 ml-2 lg:ml-0 group-hover:text-blue-700 group-hover:underline">
            {t('InvitePatientForm.qr_link')}
          </div>
        </button>
        <button
          type="button"
          onClick={onClickPermanentLink}
          className="flex focus:outline-none flex-row lg:flex-col group text-slate-600 items-center mb-4 lg:mb-0"
        >
          <IconLink className="fill-current lg:mb-2" />
          <div className="text-blue-500 ml-2 lg:ml-0 group-hover:text-blue-700 group-hover:underline">
            {t('InvitePatientForm.permanent_link')}
          </div>
        </button>
        <button
          type="button"
          onClick={onClickMultiPatientUpload}
          className="flex focus:outline-none flex-row lg:flex-col group text-slate-600 items-center"
        >
          <IconMenu className="fill-current lg:mb-2" />
          <div className="text-blue-500 ml-2 lg:ml-0 group-hover:text-blue-700 group-hover:underline">
            {t('InvitePatientForm.multi_invite')}
          </div>
        </button>
      </div>
    </div>
  );
};

InvitePatientForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isMobileNumberMode: PropTypes.bool,
  onClickMultiPatientUpload: PropTypes.func,
  onClickPermanentLink: PropTypes.func,
  onClickQRCodeLink: PropTypes.func.isRequired,
  onModeSwitch: PropTypes.func,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.array,
    PropTypes.object,
  ]),
  isLoading: PropTypes.bool,
  status: PropTypes.shape({
    apiError: PropTypes.string,
  }),
  innerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(HTMLInputElement) }),
  ]),
  email: PropTypes.string,
  mobileNumber: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  autofocus: PropTypes.bool,
  canSendSms: PropTypes.bool.isRequired,
  smsUpgradeUrl: PropTypes.string,
};

InvitePatientForm.defaultProps = {
  onClickMultiPatientUpload: () => {},
  onClickPermanentLink: () => {},
  status: { apiError: null },
  isLoading: false,
  onModeSwitch: () => {},
  smsUpgradeUrl: '',
};

export default InvitePatientForm;
